import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { FaPlus, FaTrash, FaUsersCog } from "react-icons/fa";
import api from "../../api";

export default function SchoolAdmins() {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [schools, setSchools] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    school_id: "",
    name: "",
    mobile: "",
    password: "",
    role: "admin",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loggedInRole = localStorage.getItem("role"); // super_admin / admin
  const fixedSchoolId = localStorage.getItem("school_id");

  // Fetch Admins
  const fetchAdmins = async (sid) => {
    if (!sid) {
      setAdmins([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/super_admin/school_admin/list?school_id=${sid}`);
      setAdmins(res.data?.data || []);
    } catch (err) {
      console.error("Fetch admins error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Schools
  const fetchSchools = async () => {
    if (loggedInRole === "super_admin") {
      try {
        const res = await api.get("/super_admin/school/list");
        setSchools(res.data?.data || []);
      } catch (err) {
        console.error("Fetch schools error:", err);
      }
    }
  };

  useEffect(() => {
    fetchSchools();
    if (loggedInRole === "admin" && fixedSchoolId) {
      fetchAdmins(fixedSchoolId);
      setFormData((f) => ({ ...f, school_id: fixedSchoolId }));
    }
  }, []);

  useEffect(() => {
    if (loggedInRole === "super_admin" && formData.school_id) {
      fetchAdmins(formData.school_id);
    }
  }, [formData.school_id]);

  const handleOpen = () => {
    setFormData({
      school_id: loggedInRole === "super_admin" ? "" : fixedSchoolId,
      name: "",
      mobile: "",
      password: "",
      role: "admin",
    });
    setOpenDialog(true);
  };

  const handleClose = () => setOpenDialog(false);

  const handleSave = async () => {
    if (!formData.school_id) {
      setSnackbar({
        open: true,
        message: "Please select a school!",
        severity: "warning",
      });
      return;
    }
    try {
      const res = await api.post("/super_admin/school_admin/add", formData, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data?.status) {
        setSnackbar({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        handleClose();
        fetchAdmins(formData.school_id);
      } else {
        setSnackbar({
          open: true,
          message: res.data.message,
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Admin?")) return;
    try {
      const res = await api.post(
        "/super_admin/school_admin/delete",
        { id },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.data?.status) {
        setSnackbar({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        fetchAdmins(formData.school_id || fixedSchoolId);
      } else {
        setSnackbar({
          open: true,
          message: res.data.message,
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 3,
          boxShadow: 2,
          background: "linear-gradient(135deg, #1976d2, #42a5f5)",
          color: "white",
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FaUsersCog /> School Admin Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<FaPlus />}
          onClick={handleOpen}
          sx={{ bgcolor: "white", color: "#1976d2", "&:hover": { bgcolor: "#f1f1f1" } }}
        >
          Add Admin
        </Button>
      </Paper>

      {/* School Select */}
      {loggedInRole === "super_admin" && (
        <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
          <TextField
            select
            label="Select School"
            value={formData.school_id}
            onChange={(e) => setFormData((f) => ({ ...f, school_id: e.target.value }))}
            sx={{ minWidth: 300, bgcolor: "white" }}
          >
            {schools.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {/* Loader / Empty / Table */}
      {loading ? (
        formData.school_id || loggedInRole === "admin" ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 6, color: "gray" }}>
            🎓 Please select a school to view admins
          </Box>
        )
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>ID</b></TableCell>
                <TableCell><b>Name</b></TableCell>
                <TableCell><b>Mobile</b></TableCell>
                <TableCell><b>Role</b></TableCell>
                <TableCell><b>Created At</b></TableCell>
                <TableCell align="right"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin, i) => (
                <TableRow
                  key={admin.id}
                  hover
                  sx={{ bgcolor: i % 2 === 0 ? "white" : "#fafafa" }}
                >
                  <TableCell>{admin.id}</TableCell>
                  <TableCell>{admin.name}</TableCell>
                  <TableCell>{admin.mobile}</TableCell>
                  <TableCell>{admin.role}</TableCell>
                  <TableCell>{admin.created_at}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      startIcon={<FaTrash />}
                      onClick={() => handleDelete(admin.id)}
                      sx={{ textTransform: "none" }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!admins.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "gray" }}>
                    🚫 No admins found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Dialog */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add School Admin</DialogTitle>
        <DialogContent>
          {loggedInRole === "super_admin" && (
            <TextField
              margin="dense"
              select
              label="Select School"
              fullWidth
              value={formData.school_id}
              onChange={(e) => setFormData((f) => ({ ...f, school_id: e.target.value }))}
            >
              {schools.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Mobile"
            fullWidth
            value={formData.mobile}
            inputProps={{ maxLength: 10 }}
            onChange={(e) => setFormData((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, "") }))}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={formData.password}
            onChange={(e) => setFormData((f) => ({ ...f, password: e.target.value }))}
          />
          <TextField
            margin="dense"
            select
            label="Role"
            fullWidth
            value={formData.role}
            onChange={(e) => setFormData((f) => ({ ...f, role: e.target.value }))}
          >
            <MenuItem value="super_admin">Super Admin</MenuItem>
            {/* <MenuItem value="admin">Admin</MenuItem> */}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
